import React, { ReactElement, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import IconSelector from "components/shared/images/IconSelector";
import AppModal from "components/shared/modal/AppModal";

import { postCreateCloudboxSubaccount } from "redux/handlers/addonsHandle";
import { generatePassword } from "redux/handlers/userHandler";

import { getSelectedCloudboxData } from "redux/selectors/cloudboxSelector";

import { useAppDispatch } from "hooks/reduxHook";

type subaccountType = {
 description: string;
 password: string;
 home_directory: string;
 samba_enabled: boolean;
 ssh_enabled: boolean;
 webdav_enabled: boolean;
 readonly: boolean;
 reachable_externally: boolean;
};

const CreateSubaccount = (): ReactElement => {
 const intl = useIntl();
 const dispatch = useAppDispatch();

 const cloudboxData = useSelector(getSelectedCloudboxData);

 const [openModal, setOpenModal] = useState<boolean>(false);
 const [loading, setLoading] = useState<boolean>(false);
 const [showPassword, setShowPassword] = useState<string>("password");

 const handleGeneratePassword = async () => {
  setValue("password", await generatePassword());
 };

 const handleOpen = () => {
  reset();
  setOpenModal(true);
 };
 const handleClose = () => setOpenModal(false);

 const { control, handleSubmit, reset, watch, setValue } = useForm({
  defaultValues: {
   description: "",
   password: "",
   home_directory: "",
   samba_enabled: false,
   ssh_enabled: false,
   webdav_enabled: false,
   readonly: false,
   reachable_externally: false
  }
 });

 const onSubmit: SubmitHandler<subaccountType> = async (data: subaccountType) => {
  setLoading(true);
  await dispatch(
   postCreateCloudboxSubaccount(
    cloudboxData?.id || 0,
    data.password,
    data.home_directory,
    data.samba_enabled,
    data.ssh_enabled,
    data.webdav_enabled,
    data.readonly,
    data.reachable_externally,
    data.description
   )
  );
  setLoading(false);
  handleClose();
 };

 return (
  <>
   <Button variant="kxModalConfirm" onClick={() => handleOpen()}>
    <FormattedMessage id="addons.cloudbox.createSubaccount" />
   </Button>
   <AppModal
    open={openModal}
    close={handleClose}
    title={intl.formatMessage({ id: "addons.cloudbox.createSubaccount" })}
    handleClose={handleClose}
    disabled={loading}
    handleConfirm={handleSubmit(onSubmit)}>
    <Stack>
     <Controller
      name="description"
      control={control}
      rules={{
       required: false
      }}
      render={({ field }) => (
       <TextField
        {...field}
        fullWidth={true}
        label={intl.formatMessage({ id: "addons.cloudbox.subaccountDescription" })}
        InputLabelProps={{ shrink: true }}
        sx={{ my: 2 }}
        autoComplete="off"
       />
      )}
     />
     <Controller
      name="home_directory"
      control={control}
      rules={{
       required: true
      }}
      render={({ field }) => (
       <FormControl>
        <InputLabel>
         <FormattedMessage id="addons.cloudbox.homeDirectory" />
        </InputLabel>
        <Select
         {...field}
         label={<FormattedMessage id="addons.cloudbox.homeDirectory" />}
         onChange={(e) => setValue("home_directory", e.target.value)}
         MenuProps={{
          PaperProps: {
           style: {
            maxHeight: 48 * 4.5 + 8,
            width: 250
           }
          }
         }}>
         {cloudboxData?.folders.map((fold, index) => {
          return (
           <MenuItem key={`fold-index-${index}`} value={fold}>
            {fold}
           </MenuItem>
          );
         })}
        </Select>
       </FormControl>
      )}
     />
     <Stack direction="row" spacing={2} alignItems="center" mt={2}>
      <Controller
       name="password"
       control={control}
       rules={{
        required: true,
        minLength: 8
       }}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth={true}
         label={intl.formatMessage({ id: "addons.cloudbox.password" })}
         error={watch("password") === "" || watch("password").length < 8}
         InputLabelProps={{ shrink: true }}
         sx={{ my: 2 }}
         type={showPassword}
         autoComplete="new-password"
         helperText={
          (watch("password") === "" || watch("password").length < 8) &&
          intl.formatMessage({ id: "addons.cloudbox.passwordError" })
         }
         InputProps={{
          endAdornment: (
           <InputAdornment position="end">
            <IconButton
             aria-label="toggle password visibility"
             onClick={() => {
              setShowPassword(showPassword === "text" ? "password" : "text");
             }}
             edge="end">
             {showPassword ? (
              <IconSelector icon="VisibilityOffIcon" />
             ) : (
              <IconSelector icon="VisibilityIcon" />
             )}
            </IconButton>
           </InputAdornment>
          )
         }}
        />
       )}
      />
      <Button onClick={handleGeneratePassword} sx={{ width: "40%" }}>
       <FormattedMessage id="app.generatePassword" />
      </Button>
     </Stack>
     <Controller
      name="samba_enabled"
      control={control}
      render={({ field }) => (
       <FormControlLabel
        {...field}
        control={
         <Checkbox
          checked={watch("samba_enabled")}
          onChange={(e) => {
           setValue("samba_enabled", e.target.checked);
          }}
         />
        }
        label={intl.formatMessage({ id: "addons.cloudbox.samba" })}
       />
      )}
     />
     <Controller
      name="webdav_enabled"
      control={control}
      render={({ field }) => (
       <FormControlLabel
        {...field}
        control={
         <Checkbox
          checked={watch("webdav_enabled")}
          onChange={(e) => {
           setValue("webdav_enabled", e.target.checked);
          }}
         />
        }
        label={intl.formatMessage({ id: "addons.cloudbox.webdav" })}
       />
      )}
     />
     <Controller
      name="ssh_enabled"
      control={control}
      render={({ field }) => (
       <FormControlLabel
        {...field}
        control={
         <Checkbox
          checked={watch("ssh_enabled")}
          onChange={(e) => {
           setValue("ssh_enabled", e.target.checked);
          }}
         />
        }
        label={intl.formatMessage({ id: "addons.cloudbox.ssh" })}
       />
      )}
     />
     <Controller
      name="reachable_externally"
      control={control}
      render={({ field }) => (
       <FormControlLabel
        {...field}
        control={
         <Checkbox
          checked={watch("reachable_externally")}
          onChange={(e) => {
           setValue("reachable_externally", e.target.checked);
          }}
         />
        }
        label={intl.formatMessage({ id: "addons.cloudbox.external" })}
       />
      )}
     />
     <Controller
      name="readonly"
      control={control}
      render={({ field }) => (
       <FormControlLabel
        {...field}
        control={
         <Checkbox
          checked={watch("readonly")}
          onChange={(e) => {
           setValue("readonly", e.target.checked);
          }}
         />
        }
        label={intl.formatMessage({ id: "addons.cloudbox.readonly" })}
       />
      )}
     />
    </Stack>
   </AppModal>
  </>
 );
};

export default CreateSubaccount;
